import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, isUserNotFoundError } from '../utils/auth';
import AppHeader from '../components/common/AppHeader';

// ─── Palette (Style B Sunset) ──────────────────────────────────────────────
const PALETTES = {
  B: { key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838', accent: '#FFD23F', accent2: '#3DC9B5', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5', ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4', mutedText: '#7A6B85', heroStart: '#FFB86B', heroEnd: '#FF5C5C' },
  A: { key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E', accent: '#E8A020', accent2: '#67A844', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844', ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6', mutedText: '#A67C52', heroStart: '#EAF2EA', heroEnd: '#1E5C2E' },
  C: { key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E', accent: '#FFD23F', accent2: '#16A085', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085', ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF', mutedText: '#6A6A80', heroStart: '#B8A9FF', heroEnd: '#5B4EC7' },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const CARD_BORDER = '1px solid #E8E0CC';
const CARD_SHADOW = '0 2px 6px rgba(139,111,71,0.08)';
const CARD_SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const FullMockTest = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [starsInfo, setStarsInfo] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [exams, setExams] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}`, accept: 'application/json' };
  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const [starsRes, recRes, examsRes] = await Promise.all([
          fetch(`${API}/client/stars/`, { headers }),
          fetch(`${API}/client/exams/recommended`, { headers }),
          fetch(`${API}/client/exams/`, { headers }),
        ]);
        for (const res of [starsRes, recRes, examsRes]) {
          if (res.status === 404) {
            const errorData = await res.json().catch(() => ({}));
            if (isUserNotFoundError(res, errorData)) { logout(navigate); return; }
          }
        }
        if (starsRes.ok) setStarsInfo(await starsRes.json());
        if (recRes.ok) setRecommended(await recRes.json());
        if (examsRes.ok) {
          const data = await examsRes.json();
          setExams(data.data || []);
          setTotalCount(data.count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Level is read from the title prefix ("Starters Test 002", "KET CUP 1 - Test 1"…)
  const examLevel = (e) => {
    const first = (e.title || '').trim().split(/[\s–-]/)[0].toLowerCase();
    if (first === 'starters' || first === 'starter') return 'Starters';
    if (first === 'movers' || first === 'mover') return 'Movers';
    if (first === 'flyers' || first === 'flyer') return 'Flyers';
    if (first === 'ket') return 'KET';
    return 'Khác';
  };
  const levelExams = levelFilter === 'all' ? exams : exams.filter(e => examLevel(e) === levelFilter);

  const filteredExams = levelExams.filter((e) => {
    if (activeFilter === 'available') return e.unlock_status === 'unlocked' && e.total_attempts === 0;
    if (activeFilter === 'completed') return e.total_attempts > 0;
    if (activeFilter === 'locked') return e.unlock_status === 'locked';
    return true;
  }).sort((a, b) => (a.title || '').localeCompare(b.title || '', 'vi', { numeric: true }));

  const countByFilter = {
    all: levelExams.length,
    available: levelExams.filter(e => e.unlock_status === 'unlocked' && e.total_attempts === 0).length,
    completed: levelExams.filter(e => e.total_attempts > 0).length,
    locked: levelExams.filter(e => e.unlock_status === 'locked').length,
  };

  const countByLevel = { all: exams.length };
  exams.forEach(e => { const l = examLevel(e); countByLevel[l] = (countByLevel[l] || 0) + 1; });

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FFF8E7', fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
        <AppHeader />
        <div style={{ padding: '40px 40px', maxWidth: 1440, margin: '0 auto', width: '100%' }}>
          {[180, 100, 60, 300].map((h, i) => (
            <div key={i} style={{
              background: '#fff', border: `1px solid #E8E0CC`, borderRadius: 16,
              height: h, marginBottom: 16, boxShadow: '0 2px 6px rgba(139,111,71,0.08)', opacity: 0.6,
            }} />
          ))}
        </div>
      </div>
    );
  }

  // ─── Shield renderer ────────────────────────────────────────
  const renderShields = (pct) => {
    if (!pct && pct !== 0) return null;
    let count = 1;
    if (pct >= 90) count = 5;
    else if (pct >= 80) count = 4;
    else if (pct >= 70) count = 3;
    else if (pct >= 60) count = 2;
    return (
      <div style={{ display: 'flex', gap: 2 }}>
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} style={{ fontSize: 16 }}>🛡️</span>
        ))}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8E7', fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
      <AppHeader />

      <main
        className="px-4 md:px-10"
        style={{ paddingTop: 24, paddingBottom: 60, maxWidth: 1440, margin: '0 auto', width: '100%' }}
      >

        {/* ── Row 1: Stars progress + Unlock info ───────────────── */}
        {starsInfo && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
            {/* Stars progress card */}
            <div style={{
              background: '#fff', border: CARD_BORDER, borderRadius: 16,
              padding: '16px 18px', boxShadow: CARD_SHADOW,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14, background: '#FFD166',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 30, flexShrink: 0,
                boxShadow: '0 3px 0 #E0B554',
              }}>⭐</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 600, color: '#2D2D2D', lineHeight: 1 }}>
                    {starsInfo.total_stars} <span style={{ fontSize: 14, color: '#9A9A9A', fontWeight: 500 }}>stars</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5E9E3E', letterSpacing: 0.3 }}>
                    {Math.min(starsInfo.exams_unlocked_count, totalCount)}/{totalCount} đề đã mở
                  </div>
                </div>
                <div style={{
                  height: 6, background: '#E8F3D9',
                  borderRadius: 999, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${starsInfo.next_tier_threshold ? Math.min(100, (starsInfo.total_stars / starsInfo.next_tier_threshold) * 100) : 100}%`,
                    height: '100%',
                    background: '#88C057',
                    borderRadius: 999, transition: 'width 0.4s',
                  }} />
                </div>
              </div>
            </div>

            {/* Unlock info card */}
            <div style={{
              background: 'linear-gradient(135deg, #5E9E3E 0%, #4A8830 100%)',
              color: '#fff', border: 'none', borderRadius: 16,
              padding: '18px 20px', boxShadow: CARD_SHADOW_MD,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 12, flexWrap: 'wrap',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', right: -10, top: -10, fontSize: 90, opacity: 0.12 }}>🔓</div>
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#FFD166', letterSpacing: 2 }}>
                  {starsInfo.next_tier_threshold ? 'NHÓM TIẾP THEO' : 'ĐÃ MỞ TẤT CẢ!'}
                </div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                  {starsInfo.next_tier_threshold ? `${starsInfo.next_tier_threshold} ⭐ để mở khoá` : '🎉 Chúc mừng!'}
                </div>
                {starsInfo.stars_to_next_tier != null && (
                  <div style={{ fontSize: 12, fontWeight: 500, marginTop: 4, opacity: 0.9 }}>
                    💡 Hoàn thành ~{Math.ceil(starsInfo.stars_to_next_tier / 7)} quiz nữa
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '10px 16px', border: 'none', borderRadius: 999,
                  background: '#FFD166', color: '#6B4F2B',
                  fontFamily: FONT, fontSize: 12, fontWeight: 800,
                  boxShadow: '0 3px 0 #E0B554', cursor: 'pointer',
                  flexShrink: 0, position: 'relative',
                }}
              >📝 Luyện quiz →</button>
            </div>
          </div>
        )}

        {/* ── Row 2: Recommended Exam (featured) ───────────────── */}
        {recommended?.exam && (
          <div
            className="p-5 md:p-8"
            style={{
              background: '#fff', border: CARD_BORDER, borderRadius: 20,
              boxShadow: CARD_SHADOW_MD,
              marginBottom: 24, position: 'relative', overflow: 'hidden',
            }}
          >
            {/* Accent stripe top */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 6,
              background: `linear-gradient(90deg, #E76F51, #FFD166, #5E9E3E)`,
            }} />
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{
                  display: 'inline-block',
                  background: '#FFD166', color: '#6B4F2B',
                  padding: '6px 14px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
                  marginBottom: 12, boxShadow: '0 2px 0 #E0B554',
                }}>🎯 ĐỀ XUẤT CHO BÉ</div>
                <div
                  className="text-xl md:text-2xl"
                  style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: '#2D2D2D', letterSpacing: -0.3, marginBottom: 14, lineHeight: 1.2 }}
                >
                  {recommended.exam.title}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {recommended.weak_tags_covered?.map((tag, i) => (
                    <span key={`w-${i}`} style={{
                      background: '#FCE4E8', color: '#B8431E',
                      borderRadius: 999,
                      padding: '4px 10px', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E76F51' }} /> {tag}
                    </span>
                  ))}
                  {recommended.exam.tags?.slice(0, 3).map((tag) => (
                    <span key={tag.id} style={{
                      background: '#E8F3D9', color: '#5E9E3E',
                      borderRadius: 999,
                      padding: '4px 10px', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#88C057' }} /> {tag.name}
                    </span>
                  ))}
                </div>
              </div>
              <button
                onClick={() => navigate(`/full-mock-test/${recommended.exam.id}/attempt`)}
                style={{
                  padding: '15px 28px', border: 'none', borderRadius: 999,
                  background: '#E76F51', color: '#fff',
                  fontFamily: FONT, fontSize: 15, fontWeight: 800, letterSpacing: 0.5,
                  boxShadow: '0 4px 0 #B8431E', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
                }}
              >🚀 Bắt đầu thi →</button>
            </div>
          </div>
        )}

        {/* ── Row 3a: Level filter pills ───────────────── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#9A9A9A', letterSpacing: 1, marginRight: 2 }}>CẤP ĐỘ:</span>
          {[
            { key: 'all',      label: 'Tất cả', icon: '🌟' },
            { key: 'Starters', label: 'Starters', icon: '🌱' },
            { key: 'Movers',   label: 'Movers',   icon: '🚀' },
            { key: 'Flyers',   label: 'Flyers',   icon: '🦋' },
            { key: 'KET',      label: 'KET',      icon: '🎓' },
          ].map((lv) => {
            const active = levelFilter === lv.key;
            const count = countByLevel[lv.key] || 0;
            if (lv.key !== 'all' && !count) return null;
            return (
              <button
                key={lv.key}
                onClick={() => setLevelFilter(lv.key)}
                style={{
                  padding: '8px 16px', border: active ? 'none' : CARD_BORDER, borderRadius: 999,
                  background: active ? '#3DC9B5' : '#fff',
                  color: active ? '#fff' : '#6B6B6B',
                  fontFamily: FONT, fontSize: 13, fontWeight: 700,
                  boxShadow: active ? '0 3px 0 #2BA897' : CARD_SHADOW,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 7,
                  transition: 'all 0.15s',
                }}
              >
                <span>{lv.icon}</span>
                <span>{lv.label}</span>
                <span style={{
                  background: active ? 'rgba(255,255,255,0.3)' : '#FFF8E7',
                  borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 800,
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* ── Row 3: Filter tabs (chunky pills) ───────────────── */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'Tất cả', icon: '📋' },
            { key: 'available', label: 'Chưa làm', icon: '🆕' },
            { key: 'completed', label: 'Đã làm', icon: '✅' },
            { key: 'locked', label: 'Chưa mở', icon: '🔒' },
          ].map((tab) => {
            const active = activeFilter === tab.key;
            const count = countByFilter[tab.key];
            return (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                style={{
                  padding: '10px 18px', border: active ? 'none' : CARD_BORDER, borderRadius: 999,
                  background: active ? '#FFD166' : '#fff',
                  color: active ? '#6B4F2B' : '#6B6B6B',
                  fontFamily: FONT, fontSize: 13, fontWeight: 700,
                  boxShadow: active ? '0 3px 0 #E0B554' : CARD_SHADOW,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s',
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span style={{
                  background: active ? 'rgba(255,255,255,0.65)' : '#FFF8E7',
                  borderRadius: 999,
                  padding: '1px 8px', fontSize: 11, fontWeight: 800,
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* ── Row 4: Exam Grid ───────────────── */}
        {filteredExams.length === 0 ? (
          <div style={{
            padding: '60px 24px', textAlign: 'center',
            background: '#fff', border: `2px dashed #E8E0CC`, borderRadius: 16,
          }}>
            <div style={{ fontSize: 56, opacity: 0.45 }}>🔍</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#9A9A9A', marginTop: 10 }}>
              Không có đề thi nào trong mục này
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}>
            {filteredExams.map((exam) => {
              const isCompleted = exam.total_attempts > 0;
              const isLocked = exam.unlock_status === 'locked';

              if (isLocked) {
                return (
                  <div key={exam.id} style={{
                    background: '#FAF7EF', border: CARD_BORDER,
                    borderRadius: 16, padding: 18,
                    boxShadow: CARD_SHADOW,
                    opacity: 0.7,
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <span style={{
                        background: '#EEE8D8', color: '#9A9A9A',
                        borderRadius: 999,
                        padding: '3px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1,
                      }}>🔒 ĐANG KHOÁ</span>
                      <span style={{ fontSize: 22, opacity: 0.6 }}>🔒</span>
                    </div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600, color: '#6B6B6B' }}>
                      {exam.title}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      paddingTop: 10, borderTop: `1px dashed #E8E0CC`,
                      fontSize: 12, fontWeight: 700, color: '#9A9A9A',
                    }}>
                      <span style={{ fontSize: 16 }}>⭐</span>
                      Cần {exam.stars_required} stars để mở
                    </div>
                  </div>
                );
              }

              if (isCompleted) {
                const pct = exam.highest_score ? Math.round((exam.highest_score / (exam.max_score || 100)) * 100) : 0;
                const pctColor = pct >= 80 ? '#5E9E3E' : pct >= 50 ? '#FFD166' : '#E76F51';
                return (
                  <div key={exam.id} style={{
                    background: '#fff', border: CARD_BORDER,
                    borderRadius: 16, padding: 18,
                    boxShadow: CARD_SHADOW,
                    display: 'flex', flexDirection: 'column', gap: 12,
                    borderTop: '4px solid #5E9E3E',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <span style={{
                        background: '#E8F3D9', color: '#5E9E3E',
                        borderRadius: 999,
                        padding: '3px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1,
                      }}>✅ HOÀN THÀNH</span>
                      {renderShields(pct)}
                    </div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600, color: '#2D2D2D' }}>
                      {exam.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        flex: 1, height: 6, background: '#F0ECE0',
                        borderRadius: 999, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', background: pctColor,
                          borderRadius: 999,
                        }} />
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#2D2D2D' }}>{pct}%</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {exam.last_attempt_id && (
                        <button
                          onClick={() => navigate(`/full-mock-test/${exam.last_attempt_id}/review`)}
                          style={{
                            flex: 1,
                            padding: '10px 14px', border: `2px solid #5B2E5E`, borderRadius: 999,
                            background: '#fff', color: '#5B2E5E',
                            fontFamily: FONT, fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                            cursor: 'pointer',
                          }}
                        >📖 Xem lại</button>
                      )}
                      <button
                        onClick={() => navigate(`/full-mock-test/${exam.id}/attempt`)}
                        style={{
                          flex: 1,
                          padding: '10px 14px', border: `2px solid #5E9E3E`, borderRadius: 999,
                          background: '#fff', color: '#5E9E3E',
                          fontFamily: FONT, fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                          cursor: 'pointer',
                        }}
                      >🔄 Thi lại</button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={exam.id} style={{
                  background: '#fff', border: CARD_BORDER,
                  borderRadius: 16, padding: 18,
                  boxShadow: CARD_SHADOW,
                  display: 'flex', flexDirection: 'column', gap: 12,
                  borderTop: '4px solid #FFD166',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <span style={{
                      background: '#FFF3D4', color: '#6B4F2B',
                      borderRadius: 999,
                      padding: '3px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1,
                    }}>🆕 CHƯA LÀM</span>
                    <span style={{ fontSize: 20 }}>⏱️</span>
                  </div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600, color: '#2D2D2D' }}>
                    {exam.title}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#9A9A9A' }}>
                    ⏱️ {exam.total_duration_minutes || 45} phút
                  </div>
                  <button
                    onClick={() => navigate(`/full-mock-test/${exam.id}/attempt`)}
                    style={{
                      padding: '11px 14px', border: 'none', borderRadius: 999,
                      background: '#E76F51', color: '#fff',
                      fontFamily: FONT, fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                      boxShadow: '0 3px 0 #B8431E', cursor: 'pointer',
                    }}
                  >▶ Thi ngay</button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default FullMockTest;
